import { useEffect, useId, useRef, useState } from "react";
import { useSearchUsers, type SearchUserResult } from "@/hooks/useSearchUsers";
import { cn } from "@/lib/utils";

const labelClass =
  "block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2";
const inputClass =
  "w-full px-4 py-3 border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 text-primary-950 dark:text-white focus:outline-none focus:border-primary-400 transition-colors duration-300";

export interface UserSearchComboboxProps {
  value: string;
  onChange: (value: string) => void;
  selectedUser: SearchUserResult | null;
  onSelectUser: (user: SearchUserResult | null) => void;
  excludeProjectId?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
}

export function UserSearchCombobox({
  value,
  onChange,
  selectedUser,
  onSelectUser,
  excludeProjectId,
  label = "Buscar usuário",
  placeholder = "Nome ou e-mail",
  error,
  disabled = false,
  id: idProp,
}: UserSearchComboboxProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const listId = `${id}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { users, loading, shouldSearch } = useSearchUsers(value, excludeProjectId);
  const showList = isOpen && shouldSearch && !selectedUser;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value, users.length]);

  useEffect(() => {
    if (!showList) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [showList]);

  const handleSelect = (user: SearchUserResult) => {
    onSelectUser(user);
    onChange(user.email);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleClearSelection = () => {
    onSelectUser(null);
    onChange("");
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (selectedUser) {
      if (e.key === "Escape" || e.key === "Backspace") {
        handleClearSelection();
      }
      return;
    }

    if (!showList || users.length === 0) {
      if (e.key === "Escape") setIsOpen(false);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i < users.length - 1 ? i + 1 : i));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : -1));
      return;
    }
    if (e.key === "Enter" && highlightIndex >= 0 && users[highlightIndex]) {
      e.preventDefault();
      handleSelect(users[highlightIndex]);
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>

      {selectedUser ? (
        <div className="flex items-center justify-between gap-3 border border-primary-200 bg-white px-4 py-3 dark:border-primary-800 dark:bg-primary-950">
          <div className="min-w-0">
            <p className="truncate text-sm font-light text-primary-950 dark:text-white">
              {selectedUser.fullName}
            </p>
            <p className="truncate text-xs text-primary-600 dark:text-primary-400">
              {selectedUser.email}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClearSelection}
            disabled={disabled}
            className="shrink-0 text-xs uppercase tracking-widest text-primary-600 hover:text-primary-950 dark:text-primary-400 dark:hover:text-white"
          >
            Alterar
          </button>
        </div>
      ) : (
        <>
          <input
            id={id}
            type="search"
            autoComplete="off"
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(inputClass, error && "border-red-500 dark:border-red-500")}
            onChange={(e) => {
              onChange(e.target.value);
              onSelectUser(null);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
          />

          {showList && (
            <ul
              id={listId}
              role="listbox"
              className="absolute z-50 mt-1 max-h-56 w-full overflow-auto border border-primary-200 bg-white shadow-md dark:border-primary-800 dark:bg-primary-950"
            >
              {loading && (
                <li className="px-4 py-3 text-sm font-light text-primary-500">
                  Buscando...
                </li>
              )}
              {!loading && users.length === 0 && (
                <li className="px-4 py-3 text-sm font-light text-primary-500">
                  Nenhum usuário encontrado
                </li>
              )}
              {!loading &&
                users.map((user, index) => (
                  <li
                    key={user.id}
                    role="option"
                    aria-selected={highlightIndex === index}
                    className={cn(
                      "cursor-pointer px-4 py-3 transition-colors",
                      highlightIndex === index
                        ? "bg-primary-100 dark:bg-primary-800"
                        : "hover:bg-primary-50 dark:hover:bg-primary-900"
                    )}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(user);
                    }}
                  >
                    <p className="text-sm font-light text-primary-950 dark:text-white">
                      {user.fullName}
                    </p>
                    <p className="text-xs text-primary-600 dark:text-primary-400">
                      {user.email}
                    </p>
                  </li>
                ))}
            </ul>
          )}
        </>
      )}

      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
